import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { apifetch } from '../services/interceptors';

/**
 * ExportQueueContext - Global state for background PDF exports
 * Persists across page navigation so users can see export progress everywhere
 */
const ExportQueueContext = createContext();

export const useExportQueue = () => {
  const context = useContext(ExportQueueContext);
  if (!context) {
    throw new Error('useExportQueue must be used within ExportQueueProvider');
  }
  return context;
};

export const ExportQueueProvider = ({ children }) => {
  const [exportQueue, setExportQueue] = useState([]);
  const exportQueueRef = useRef([]);

  /**
   * Add a new export to the queue
   */
  const addExport = useCallback((exportEntry) => {
    setExportQueue(prev => {
      const updated = [exportEntry, ...prev];
      exportQueueRef.current = updated;
      return updated;
    });
  }, []);

  /**
   * Update an existing export in the queue
   */
  const updateExport = useCallback((exportId, updates) => {
    setExportQueue(prev => {
      const updated = prev.map(exp =>
        exp.id === exportId
          ? { ...exp, ...updates }
          : exp
      );
      exportQueueRef.current = updated;
      return updated;
    });
  }, []);

  /**
   * Remove an export from the queue
   */
  const removeExport = useCallback((exportId) => {
    setExportQueue(prev => {
      const exportItem = prev.find(exp => exp.id === exportId);

      // Clean up object URL if it exists
      if (exportItem && exportItem.objectURL) {
        URL.revokeObjectURL(exportItem.objectURL);
      }

      const updated = prev.filter(exp => exp.id !== exportId);
      exportQueueRef.current = updated;
      return updated;
    });
  }, []);

  /**
   * Process a background export
   * This function runs the actual export and updates progress
   */
  const processBackgroundExport = useCallback(async (exportEntryId, htmlPages, css, format, token, fileUrl, templateName, orientation = 'portrait') => {
    try {
      if (!fileUrl) {
        throw new Error('File URL is required for export');
      }

      // Prepare request body with HTML pages
      const requestBody = {
        fileUrl: fileUrl,
        htmlPages: htmlPages, // Send captured HTML
        css: css, // Send extracted CSS
        format: format,
        templateName: templateName || 'Custom Report',
        orientation: orientation // 'portrait' or 'landscape'
      };

      console.log(`[processBackgroundExport] ${exportEntryId} - Sending job to server...`);
      console.log(`[processBackgroundExport] Orientation: ${orientation}`);

      // Update progress: Uploading
      updateExport(exportEntryId, {
        stage: 'Initiating export job...',
        progress: 10
      });

      // Step 1: Start the Job
      const startResponse = await apifetch('/s3/api/export-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!startResponse.ok) {
        const errorText = await startResponse.text();
        throw new Error(`Server returned ${startResponse.status}: ${errorText}`);
      }

      const startData = await startResponse.json();
      const serverJobId = startData.jobId;

      console.log(`[processBackgroundExport] ${exportEntryId} - Job started on server with ID: ${serverJobId}`);

      // Update export with server job ID
      updateExport(exportEntryId, {
        serverJobId: serverJobId,
        stage: 'Processing on server...',
        progress: 20
      });

      // Step 2: Poll for Status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await apifetch(`/s3/api/export-status/${serverJobId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            }
          });

          if (!statusResponse.ok) {
            // If 404, maybe job is gone or server restarted
            if (statusResponse.status === 404) {
              clearInterval(pollInterval);
              updateExport(exportEntryId, { status: 'error', error: 'Export job lost on server' });
              return;
            }
            console.warn(`[processBackgroundExport] Polling failed: ${statusResponse.status}`);
            return; // Retry next time
          }

          const statusData = await statusResponse.json();
          console.log(`[processBackgroundExport] Job ${serverJobId} status: ${statusData.status}, progress: ${statusData.progress}`);

          if (statusData.status === 'completed') {
            clearInterval(pollInterval);

            // Job complete - Prepare for download
            // We don't fetch the blob immediately to save memory. 
            // We just note it's ready. The download button will trigger the fetch.
            // OR to match current behavior ("Save" modal behavior), we fetch the blob now.
            // Let's fetch the blob now so the user can just click "Save" instantly.

            updateExport(exportEntryId, {
              stage: 'Downloading file...',
              progress: 95
            });

            const downloadResponse = await apifetch(`/s3/api/download-export/${serverJobId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!downloadResponse.ok) throw new Error('Failed to download completed file');

            const blob = await downloadResponse.blob();

            // Verify PDF header
            const header = await blob.slice(0, 5).text();
            if (!header.startsWith('%PDF-')) {
              throw new Error('Invalid PDF file received');
            }

            const objectURL = URL.createObjectURL(blob);

            updateExport(exportEntryId, {
              status: 'completed',
              stage: 'Ready to download',
              progress: 100,
              blob: blob,
              objectURL: objectURL,
              size: blob.size,
              serverJobId: serverJobId // Keep tracking ID
            });

          } else if (statusData.status === 'failed') {
            clearInterval(pollInterval);
            throw new Error(statusData.error || 'Server reported job failure');
          } else {
            // Still processing - update progress with backend's detailed stage message
            updateExport(exportEntryId, {
              progress: statusData.progress || 30,
              stage: statusData.stage || 'Generating PDF...' // Use backend's stage message (e.g., "Rendering page 50 of 261...")
            });
          }

        } catch (pollError) {
          console.error('Polling error:', pollError);
          // Don't kill the job on single poll error, maybe network blip
        }
      }, 2000); // Poll every 2 seconds

      // Store interval ID in export entry? No, we can't easily. 
      // But since this is a closure, it works for this session.
      // Ideally we should store active intervals in a ref to clear them on unmount, 
      // but ExportQueueContext persists, so it's okay.

    } catch (error) {
      console.error(`[processBackgroundExport] ${exportEntryId} - Error:`, error);

      // Update progress: Error
      updateExport(exportEntryId, {
        status: 'error',
        error: error.message || 'Export failed'
      });
    }
  }, [updateExport]);

  /**
   * Download a completed export
   */
  const downloadExport = useCallback((exportId) => {
    const exportItem = exportQueue.find(exp => exp.id === exportId);

    if (!exportItem || !exportItem.objectURL) {
      console.error(`[downloadExport] Export ${exportId} not found or no object URL`);
      return;
    }

    console.log(`[downloadExport] Downloading ${exportId}...`);

    // Trigger download
    const link = document.createElement('a');
    link.href = exportItem.objectURL;
    const timestamp = Date.now();
    // Use proper name
    const safeName = (exportItem.name || 'report').replace(/[^a-z0-9]/gi, '_');
    link.setAttribute('download', `${safeName}_${timestamp}.${exportItem.format || 'pdf'}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log(`[downloadExport] Download triggered for ${exportItem.name}`);
  }, [exportQueue]);

  const value = {
    exportQueue,
    exportQueueRef,
    addExport,
    updateExport,
    removeExport,
    processBackgroundExport,
    downloadExport
  };

  return (
    <ExportQueueContext.Provider value={value}>
      {children}
    </ExportQueueContext.Provider>
  );
};

export default ExportQueueContext;
